import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Asking the server again, on a schedule the customer chose.
 *
 * Polling is the easiest thing in a browser to get wrong, and every way of
 * getting it wrong costs somebody battery or data on a phone. The five rules
 * below are the whole of this file; the rest is bookkeeping.
 *
 *  - **Off unless asked.** There is no default interval. A dashboard that
 *    polls because polling is nice is a dashboard that drains a phone in a
 *    pocket.
 *  - **One request at a time.** A slow response on a slow connection must not
 *    stack another request behind it; the next tick is scheduled *after* the
 *    last one settles, not on a fixed clock.
 *  - **Nothing while the page is hidden.** A backgrounded tab does not need
 *    fresh numbers. It refreshes once on becoming visible, because the numbers
 *    it is showing are then as old as the time it spent hidden.
 *  - **Back off after a failure**, doubling to a ceiling, and recover the
 *    moment a request succeeds. A server having a bad minute must not be
 *    asked sixty times during it.
 *  - **A failure never replaces the data.** The last good value stays on
 *    screen with the time it was fetched, and the failure is reported beside
 *    it. Blanking a screen because one poll failed turns a transient network
 *    blip into "all your metrics are zero", which is both wrong and alarming.
 */

export type PollInterval = 0 | 30_000 | 60_000 | 300_000;

export interface Polled<T> {
  /** The last successful value. Undefined only before the first one. */
  readonly data: T | undefined;
  /** When `data` was fetched. Not "now" — the age of what is on screen. */
  readonly fetchedAt: Date | undefined;
  readonly loading: boolean;
  /** Set when the most recent attempt failed; `data` is still the last good one. */
  readonly failed: boolean;
  /** Consecutive failures, for a screen that wants to say how bad it is. */
  readonly failures: number;
  refresh: () => void;
}

const MAX_BACKOFF_MS = 300_000;

export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: PollInterval,
  options: { readonly now?: () => Date } = {},
): Polled<T> {
  const [data, setData] = useState<T | undefined>();
  const [fetchedAt, setFetchedAt] = useState<Date | undefined>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [failures, setFailures] = useState(0);

  // Refs, not state: changing these must not re-render, and the scheduler
  // reads them from inside a timer that outlives any one render.
  const inFlight = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const disposed = useRef(false);
  const fetcherRef = useRef(fetcher);
  const now = options.now ?? (() => new Date());

  // In an effect rather than during render: a ref written while rendering is
  // a side effect in a function React may call more than once. The initial
  // `useRef` already holds the first value, and this effect runs before any
  // later poll can read it.
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const run = useCallback(async (): Promise<void> => {
    // The single-flight guard. Without it a 20-second response and a
    // 30-second interval quietly become two concurrent requests, then three.
    if (inFlight.current || disposed.current) return;
    inFlight.current = true;
    try {
      const value = await fetcherRef.current();
      if (disposed.current) return;
      setData(value);
      setFetchedAt(now());
      setFailed(false);
      setFailures(0);
    } catch {
      if (disposed.current) return;
      // Deliberately not touching `data`. See the fifth rule.
      setFailed(true);
      setFailures((count) => count + 1);
    } finally {
      inFlight.current = false;
      if (!disposed.current) setLoading(false);
    }
    // `now` is rebuilt each render by design and would defeat the memo; the
    // value it returns is read at call time either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    void run();
  }, [run]);

  // First load, always, whatever the interval is. "Off" means no *repeat*.
  useEffect(() => {
    disposed.current = false;
    void run();
    return () => {
      disposed.current = true;
    };
  }, [run]);

  useEffect(() => {
    if (intervalMs === 0) return;

    const clear = (): void => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    };

    const schedule = (): void => {
      clear();
      if (document.visibilityState === 'hidden') return;
      // After a failure, wait longer each time: the interval doubled per
      // consecutive failure, to a five-minute ceiling.
      const delay = failed
        ? Math.min(intervalMs * 2 ** Math.min(failures, 6), MAX_BACKOFF_MS)
        : intervalMs;
      timer.current = setTimeout(() => {
        void run().finally(schedule);
      }, delay);
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        // What is on screen is as stale as the time the tab spent hidden.
        void run().finally(schedule);
      } else {
        clear();
      }
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, failed, failures, run]);

  return { data, fetchedAt, loading, failed, failures, refresh };
}
